/**
 * Frontend single exam gate runtime.
 */

import * as lpUtils from 'lpExamsAssetsJsPath/utils.js';

export class SingleExam {
	static selectors = {
		startButton: '.lp-exam-start-button',
		startTarget: '.lp-single-exam__actions.lp-target, .lp-course-exam-card__actions.lp-target',
		startNotice: '.lp-single-exam__notice',
		guestInfo: '.lp-single-exam__guest-info',
		guestName: 'input[name="guest_name"]',
		guestEmail: 'input[name="guest_email"]',
		guestPhone: 'input[name="guest_phone"]',
		guestHoneypot: 'input[name="website"]',
		passwordForm: '.lp-single-exam__password',
		passwordInput: 'input[name="lp_exam_password"]',
		passwordToggle: '.lp-single-exam__password-toggle',
		passwordNotice: '.lp-single-exam__password-notice',
	};

	static guestProfileKey = 'lp-exam-guest-profile';

	constructor() {
		this.starting = false;
		this.redirecting = false;
		this.init();
	}

	init() {
		this.events();
		this.prefillGuestInfo();
	}

	events() {
		if ( SingleExam._loadedEvents ) {
			return;
		}

		SingleExam._loadedEvents = this;

		lpUtils.listenElementCreated( ( node ) => {
			if ( typeof node.matches !== 'function' ) {
				return;
			}

			const hasGuestInfo =
				node.matches( SingleExam.selectors.guestInfo ) ||
				( typeof node.querySelector === 'function' && node.querySelector( SingleExam.selectors.guestInfo ) );

			if ( hasGuestInfo ) {
				this.prefillGuestInfo();
			}
		} );

		lpUtils.eventHandlers( 'click', [
			{ selector: SingleExam.selectors.startButton, class: this, callBack: this.startExam.name },
			{
				selector: SingleExam.selectors.passwordToggle,
				class: this,
				callBack: this.togglePasswordVisibility.name,
			},
		] );

		lpUtils.eventHandlers( 'submit', [
			{
				selector: SingleExam.selectors.passwordForm,
				class: this,
				callBack: this.verifyExamPassword.name,
			},
		] );
	}

	showNotice( target, message = '', status = 'error' ) {
		const notice = target?.querySelector( SingleExam.selectors.startNotice );
		if ( ! notice ) {
			return;
		}

		notice.textContent = message;
		notice.classList.toggle( 'is-error', status !== 'success' );
		notice.classList.toggle( 'is-success', status === 'success' );
	}

	showPasswordNotice( form, message = '', status = 'error' ) {
		const notice = form?.querySelector( SingleExam.selectors.passwordNotice );
		if ( ! notice ) {
			return;
		}

		notice.textContent = message;
		notice.classList.toggle( 'is-error', status !== 'success' );
		notice.classList.toggle( 'is-success', status === 'success' );
	}

	togglePasswordVisibility( args ) {
		const { target } = args;
		const button = target.closest( SingleExam.selectors.passwordToggle );
		const form = button?.closest( SingleExam.selectors.passwordForm );
		const input = form?.querySelector( SingleExam.selectors.passwordInput );

		if ( ! button || ! input ) {
			return;
		}

		const showPassword = input.type === 'password';
		input.type = showPassword ? 'text' : 'password';
		button.setAttribute( 'aria-pressed', showPassword ? 'true' : 'false' );
		button.textContent = showPassword
			? button.dataset.hideLabel || 'Hide'
			: button.dataset.showLabel || 'Show';
		input.focus();
	}

	verifyExamPassword( args ) {
		const { e, target } = args;
		const form = target.closest( SingleExam.selectors.passwordForm );

		if ( ! form?.dataset.send || ! window.lpAJAXG ) {
			return;
		}

		e.preventDefault();

		const input = form.querySelector( SingleExam.selectors.passwordInput );
		const button = form.querySelector( 'button[type="submit"]' );
		const password = input?.value || '';
		if ( ! password ) {
			this.showPasswordNotice( form, 'Please enter the exam password.' );
			return;
		}

		let dataSend = {};
		try {
			dataSend = JSON.parse( form.dataset.send );
		} catch ( error ) {
			this.showPasswordNotice( form, 'Invalid exam data.' );
			return;
		}

		dataSend.password = password;
		if ( button ) {
			button.disabled = true;
			lpUtils.lpSetLoadingEl( button, 1 );
		}
		this.showPasswordNotice( form, '' );

		window.lpAJAXG.fetchAJAX( dataSend, {
			success: ( response ) => {
				if ( response.status !== 'success' ) {
					this.showPasswordNotice( form, response.message || 'Incorrect exam password.' );
					return;
				}

				this.showPasswordNotice( form, response.message || '', 'success' );
				if ( response.data?.reload !== false ) {
					window.location.reload();
				}
			},
			error: () => this.showPasswordNotice( form, 'Request failed. Please try again.' ),
			completed: () => {
				if ( button ) {
					button.disabled = false;
					lpUtils.lpSetLoadingEl( button, 0 );
				}
			},
		} );
	}

	startExam( args ) {
		const { e, target } = args;
		const button = target.closest( SingleExam.selectors.startButton );
		const examTarget = button?.closest( SingleExam.selectors.startTarget );

		if ( ! button || ! examTarget?.dataset.send || ! window.lpAJAXG ) {
			return;
		}

		e.preventDefault();
		if ( this.starting || button.disabled ) {
			return;
		}

		let dataSend = {};
		try {
			dataSend = JSON.parse( examTarget.dataset.send );
		} catch ( error ) {
			this.showNotice( examTarget, 'Invalid exam data.' );
			return;
		}

		const guestInfo = this.collectGuestInfo( examTarget, dataSend );
		if ( guestInfo === false ) {
			return;
		}

		dataSend = {
			...dataSend,
			...guestInfo,
		};

		this.starting = true;
		button.disabled = true;
		lpUtils.lpSetLoadingEl( button, 1 );
		this.showNotice( examTarget, '' );

		if ( dataSend.guest_mode === true && dataSend.direct_action ) {
			this.startGuestExam( dataSend, examTarget, button );
			return;
		}

		window.lpAJAXG.fetchAJAX( dataSend, {
			success: ( response ) => {
				if ( response.status !== 'success' ) {
					this.showNotice( examTarget, response.message || 'Could not start exam.' );
					return;
				}

				this.showNotice( examTarget, response.message || '', 'success' );
				if ( response.data?.redirect_url ) {
					this.redirecting = true;
					window.location.href = response.data.redirect_url;
				}
			},
			error: () => this.showNotice( examTarget, 'Request failed. Please try again.' ),
			completed: () => {
				if ( this.redirecting ) {
					return;
				}

				this.starting = false;
				button.disabled = false;
				lpUtils.lpSetLoadingEl( button, 0 );
			},
		} );
	}

	collectGuestInfo( examTarget, dataSend ) {
		if ( dataSend.guest_info_required !== true ) {
			return {};
		}

		const info = examTarget.querySelector( SingleExam.selectors.guestInfo );
		const nameInput = info?.querySelector( SingleExam.selectors.guestName );
		const emailInput = info?.querySelector( SingleExam.selectors.guestEmail );
		const phoneInput = info?.querySelector( SingleExam.selectors.guestPhone );
		const honeypotInput = info?.querySelector( SingleExam.selectors.guestHoneypot );
		const guestName = nameInput?.value.trim() || '';
		const guestEmail = emailInput?.value.trim() || '';
		const guestPhone = phoneInput?.value.trim() || '';

		if ( ! guestName ) {
			this.showNotice( examTarget, 'Please enter your full name to start the exam.' );
			nameInput?.focus();
			return false;
		}

		if ( guestEmail && ! this.isValidEmail( guestEmail, emailInput ) ) {
			this.showNotice( examTarget, 'Please enter a valid email address.' );
			emailInput?.focus();
			return false;
		}

		const guestData = {
			guest_name: guestName,
			guest_email: guestEmail,
			guest_phone: guestPhone,
			website: honeypotInput?.value || '',
		};

		this.saveGuestProfile( guestData );

		return guestData;
	}

	readGuestProfile() {
		try {
			const raw = window.localStorage.getItem( SingleExam.guestProfileKey );
			if ( ! raw ) {
				return null;
			}

			const profile = JSON.parse( raw );
			return profile && typeof profile === 'object' ? profile : null;
		} catch ( error ) {
			return null;
		}
	}

	saveGuestProfile( info ) {
		const profile = {
			guest_name: info.guest_name || '',
			guest_email: info.guest_email || '',
			guest_phone: info.guest_phone || '',
		};

		try {
			window.localStorage.setItem( SingleExam.guestProfileKey, JSON.stringify( profile ) );
		} catch ( error ) {
			// Ignore storage failures; pre-filling is a convenience only.
		}
	}

	prefillGuestInfo() {
		const profile = this.readGuestProfile();
		if ( ! profile ) {
			return;
		}

		const fields = {
			[ SingleExam.selectors.guestName ]: profile.guest_name,
			[ SingleExam.selectors.guestEmail ]: profile.guest_email,
			[ SingleExam.selectors.guestPhone ]: profile.guest_phone,
		};

		document.querySelectorAll( SingleExam.selectors.guestInfo ).forEach( ( section ) => {
			Object.entries( fields ).forEach( ( [ selector, value ] ) => {
				const input = section.querySelector( selector );
				if ( input && ! input.value && value ) {
					input.value = value;
				}
			} );
		} );
	}

	isValidEmail( email, input = null ) {
		if ( input && typeof input.checkValidity === 'function' ) {
			return input.checkValidity();
		}

		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( email );
	}

	startGuestExam( dataSend, examTarget, button ) {
		window.lpAJAXG.fetchAJAX(
			{ ...dataSend, action: dataSend.direct_action },
			{
				success: ( response ) => {
					if ( response.status !== 'success' ) {
						this.showNotice( examTarget, response.message || 'Could not start exam.' );
						return;
					}

					const data = response.data || {};
					if ( ! data.attempt_token || ! data.storage_key || ! data.workspace_url ) {
						this.showNotice( examTarget, response.message || 'Could not start exam.' );
						return;
					}

					if ( ! this.storeGuestAttemptDraft( data ) ) {
						this.showNotice(
							examTarget,
							'Your browser cannot save this guest attempt. Please enable browser storage and try again.'
						);
						return;
					}

					this.showNotice( examTarget, response.message || '', 'success' );
					this.redirecting = true;
					window.location.href = data.workspace_url;
				},
				error: () => this.showNotice( examTarget, 'Request failed. Please try again.' ),
				completed: () => {
					if ( this.redirecting ) {
						return;
					}

					this.starting = false;
					button.disabled = false;
					lpUtils.lpSetLoadingEl( button, 0 );
				},
			}
		);
	}

	storeGuestAttemptDraft( data ) {
		const draft = {
			version: 1,
			exam_id: data.exam_id,
			exam_slug: data.exam_slug,
			settings_hash: data.settings_hash,
			question_set_hash: data.question_set_hash,
			attempt_token: data.attempt_token,
			guest_info: data.guest_info || null,
			answers: {},
			current_question_index: 0,
			marked_questions: [],
			submit_attempted: false,
			started_at_gmt: data.started_at_gmt,
			deadline_gmt: data.deadline_gmt,
			hard_deadline_gmt: data.hard_deadline_gmt,
			expires_at: data.expires_at,
			updated_at: Math.floor( Date.now() / 1000 ),
		};

		try {
			window.localStorage.setItem( data.storage_key, JSON.stringify( draft ) );
			this.clearStoredGuestResult( data.storage_key );
			return true;
		} catch ( error ) {
			try {
				window.sessionStorage.setItem( data.storage_key, JSON.stringify( draft ) );
				this.clearStoredGuestResult( data.storage_key );
				return true;
			} catch ( sessionError ) {
				return false;
			}
		}
	}

	clearStoredGuestResult( storageKey ) {
		const resultKey = `${ storageKey }::result`;
		try {
			window.localStorage.removeItem( resultKey );
		} catch ( error ) {
			// Ignore storage cleanup failures.
		}
		try {
			window.sessionStorage.removeItem( resultKey );
		} catch ( error ) {
			// Ignore storage cleanup failures.
		}
	}
}
